// ═══════════════════════════════════════════════════════════════════════════
// PAGE: PROJECT — modales historique + ajout risque (lot B.10)
// Migration S5.1 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// showRiskHistory (avec Chart.js), renderHistoryChart, showAddRiskModal
// (création/édition risque projet).
//
// Bare refs : ps, api, openModal, closeModal, fmtDate, sevColor, esc, t,
// toast, safeWarn, Chart (CDN).

interface RiskHistoryEntry {
  revision_name: string;
  date_start: string;
  date_end?: string | null;
  is_closed?: boolean;
  is_relevant?: boolean;
  probability?: number | null;
  impact?: number | null;
  severity: number | null;
  owner_name?: string | null;
}

interface RiskListLiteForModal {
  id: number;
  name?: string;
}

async function showRiskHistory(riskId: number): Promise<void> {
  const risks = ps.risks || [];
  const risk = risks.find((r) => r.id === riskId);
  const titleLabel = risk
    ? [risk.code, risk.consequence].filter(Boolean).join(' ') ||
      risk.category ||
      t('pageProject.history.fallback', { id: riskId })
    : t('pageProject.history.fallbackTitle');
  const title = titleLabel;
  openModal(
    t('pageProject.history.title', { label: title }),
    `<div class="loading">${esc(t('common.loading'))}</div>`,
    () => closeModal(),
    t('pageProject.history.btnClose'),
  );
  const overlay = document.getElementById('modal-overlay');
  if (overlay) {
    const m = overlay.querySelector<HTMLElement>('.modal');
    if (m) {
      m.style.maxWidth = '900px';
      m.style.width = '95vw';
    }
  }
  const confirmBtn = document.getElementById('modal-confirm') as HTMLButtonElement | null;
  if (confirmBtn) {
    confirmBtn.textContent = t('pageProject.history.btnClose');
    confirmBtn.onclick = () => closeModal();
  }
  const cancelBtn = document.getElementById('modal-cancel') as HTMLButtonElement | null;
  if (cancelBtn) cancelBtn.style.display = 'none';

  try {
    if (!ps.project) return;
    const history = (await api.getRiskHistory(ps.project.id, riskId)) as RiskHistoryEntry[];
    const body = document.getElementById('modal-body');
    if (!body) return;

    if (!history.length) {
      body.innerHTML = `<p style="color:var(--text3);font-style:italic">${esc(t('pageProject.history.empty'))}</p>`;
      return;
    }

    let rows = '';
    for (let i = 0; i < history.length; i++) {
      const h = history[i];
      if (!h) continue;
      const prev = history[i - 1];
      const prevSev = i > 0 && prev && prev.severity !== null ? prev.severity : null;
      const sev = h.severity;
      const sevCol = sev !== null ? sevColor(sev) : '';

      let trend = '';
      if (!h.is_relevant) {
        trend = `<span style="color:var(--text3)">${esc(t('pageProject.history.np'))}</span>`;
      } else if (i === 0 || prevSev === null) {
        trend = '<span style="color:var(--text3)">—</span>';
      } else if (prevSev !== null && prevSev < 4 && sev !== null && sev >= 4) {
        trend = `<span style="background:#facc15;color:#713f12;font-size:10px;font-weight:700;padding:1px 5px;border-radius:8px">${esc(t('pageProject.trend.new'))}</span>`;
      } else if (sev !== null && prevSev !== null && sev > prevSev) {
        trend = '<span style="color:var(--danger);font-size:15px">↑</span>';
      } else if (sev !== null && prevSev !== null && sev < prevSev) {
        trend = '<span style="color:var(--ok);font-size:15px">↓</span>';
      } else {
        trend = '<span style="font-size:15px">→</span>';
      }

      const sevDisplay = h.is_relevant
        ? `<span class="text-mono" style="font-size:16px;font-weight:600;color:${sevCol}">
            ${sev !== null ? sev : '—'}</span>
           <span style="font-size:11px;color:var(--text3)">(${h.probability ?? '—'} × ${h.impact ?? '—'})</span>`
        : `<span style="color:var(--text3);font-size:12px">${esc(t('pageProject.history.notRelevant'))}</span>`;

      const closedLabel = h.is_closed
        ? esc(fmtDate(h.date_end))
        : `<em style="color:var(--accent)">${esc(t('pageProject.history.inProgress'))}</em>`;

      rows += `<tr>
        <td style="white-space:nowrap">${esc(h.revision_name)}</td>
        <td style="white-space:nowrap">${esc(fmtDate(h.date_start))}</td>
        <td style="white-space:nowrap">${closedLabel}</td>
        <td>${sevDisplay}</td>
        <td style="text-align:center">${trend}</td>
        <td style="font-size:12px;color:var(--text2)">${esc(h.owner_name || '—')}</td>
      </tr>`;
    }

    body.innerHTML = `
      <canvas id="history-chart" style="margin-bottom:16px;max-height:200px"></canvas>
      <div style="overflow-x:auto">
        <table style="width:100%;border-collapse:collapse;font-size:13px">
          <thead><tr>
            <th style="text-align:left;padding:6px 8px;border-bottom:1px solid var(--border);font-size:11px;color:var(--text3);text-transform:uppercase">${esc(t('pageProject.history.colRev'))}</th>
            <th style="text-align:left;padding:6px 8px;border-bottom:1px solid var(--border);font-size:11px;color:var(--text3);text-transform:uppercase">${esc(t('pageProject.history.colStart'))}</th>
            <th style="text-align:left;padding:6px 8px;border-bottom:1px solid var(--border);font-size:11px;color:var(--text3);text-transform:uppercase">${esc(t('pageProject.history.colEnd'))}</th>
            <th style="text-align:left;padding:6px 8px;border-bottom:1px solid var(--border);font-size:11px;color:var(--text3);text-transform:uppercase">${esc(t('pageProject.history.colSev'))}</th>
            <th style="text-align:center;padding:6px 8px;border-bottom:1px solid var(--border);font-size:11px;color:var(--text3);text-transform:uppercase">${esc(t('pageProject.history.colTrend'))}</th>
            <th style="text-align:left;padding:6px 8px;border-bottom:1px solid var(--border);font-size:11px;color:var(--text3);text-transform:uppercase">${esc(t('pageProject.history.colOwner'))}</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;

    body.querySelectorAll<HTMLElement>('tbody td').forEach((td) => {
      td.style.padding = '7px 8px';
      td.style.borderBottom = '1px solid var(--border)';
      td.style.verticalAlign = 'middle';
    });

    const labels = history.map((h) =>
      h.is_closed ? fmtDate(h.date_end) : t('pageProject.history.inProgress'),
    );
    const values = history.map((h) => (h.is_relevant && h.severity !== null ? h.severity : null));

    const doChart = (): void => renderHistoryChart(labels, values);
    if (!window.Chart) {
      if (!document.querySelector('script[src*="Chart.js"]')) {
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';
        s.onload = doChart;
        document.head.appendChild(s);
      } else {
        const wait = setInterval(() => {
          if (window.Chart) {
            clearInterval(wait);
            doChart();
          }
        }, 50);
      }
    } else {
      doChart();
    }
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : t('common.error');
    const body = document.getElementById('modal-body');
    if (body) body.innerHTML = `<div class="error">${esc(msg)}</div>`;
  }
}

function renderHistoryChart(labels: Array<string>, values: Array<number | null>): void {
  const canvas = document.getElementById('history-chart') as HTMLCanvasElement | null;
  const ChartCtor = window.Chart as
    (new (canvas: HTMLCanvasElement, config: unknown) => { destroy(): void }) | undefined;
  const ChartGet =
    (window.Chart as unknown as {
      getChart?: (c: HTMLCanvasElement) => { destroy(): void } | undefined;
    }) || undefined;
  if (!canvas || !ChartCtor) return;
  const existing = ChartGet?.getChart?.(canvas);
  if (existing) existing.destroy();

  const pointColors = values.map((v) => {
    if (v === null) return 'rgba(0,0,0,0)';
    if (v >= 12) return '#dc2626';
    if (v >= 8) return '#d97706';
    if (v >= 4) return '#0891b2';
    return '#16a34a';
  });

  new ChartCtor(canvas, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: t('pageProject.chart.label'),
          data: values,
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37,99,235,0.07)',
          borderWidth: 2,
          pointRadius: 5,
          pointBackgroundColor: pointColors,
          pointBorderColor: pointColors,
          tension: 0.3,
          spanGaps: false,
        },
      ],
    },
    options: {
      responsive: true,
      plugins: { legend: { display: false } },
      scales: {
        y: { min: 0, max: 20, ticks: { stepSize: 4 }, grid: { color: 'rgba(0,0,0,0.06)' } },
        x: { grid: { display: false } },
      },
    },
  });
}

async function showAddRiskModal(existingRisk: PageRisk | null = null): Promise<void> {
  const stages = ps.stages || [];
  const isNew = !existingRisk;
  const defaultStages = isNew
    ? ps.project && ps.project.current_stage
      ? [ps.project.current_stage]
      : ps.selectedStage
        ? [ps.selectedStage]
        : []
    : existingRisk?.stages || [];
  const r: PageRisk =
    existingRisk ||
    ({
      id: 0,
      code: '',
      stages: defaultStages,
      category: '',
      visibility: 'publique',
      cause: '',
      consequence: '',
    } as PageRisk);
  if (isNew) r.stages = defaultStages;

  const existingCategories = Array.from(
    new Set((ps.risks || []).map((rr) => rr.category).filter(Boolean) as string[]),
  ).sort();
  const datalistOptions = existingCategories
    .map((c) => `<option value="${esc(c)}"></option>`)
    .join('');

  let riskLists: RiskListLiteForModal[] = [];
  if (isNew) {
    try {
      riskLists = (await api.getRiskLists()) as RiskListLiteForModal[];
    } catch (e: unknown) {
      safeWarn('getRiskLists pour propagation standard', e);
      riskLists = [];
    }
  }

  const stageChecks = stages
    .map(
      (s) => `
    <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
      <input type="checkbox" name="risk-stages" value="${esc(s.name)}" ${(r.stages || []).includes(s.name) ? 'checked' : ''}>
      ${esc(s.name)}
    </label>`,
    )
    .join('');

  const standardSection =
    isNew && riskLists.length > 0
      ? `
    <div class="form-group" style="margin-top:12px;padding-top:12px;border-top:1px solid var(--border)">
      <label style="display:flex;align-items:center;gap:6px;cursor:pointer">
        <input type="checkbox" id="rm-also-standard">
        <span style="font-weight:500">${esc(t('pageProject.addRisk.alsoStandard'))}</span>
      </label>
      <div id="rm-standard-lists" style="display:none;margin-top:8px;padding-left:22px;border-left:2px solid var(--accent-bg)">
        <div style="font-size:11px;color:var(--text3);margin-bottom:6px">${esc(t('pageProject.addRisk.copyTo'))}</div>
        ${riskLists
          .map(
            (rl) => `
          <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px;cursor:pointer">
            <input type="checkbox" class="rm-rl-chk" value="${rl.id}">
            <span>${esc(rl.name || t('pageProject.addRisk.listFallback', { id: rl.id }))}</span>
          </label>`,
          )
          .join('')}
      </div>
    </div>`
      : '';

  const categoryFieldHtml = isNew
    ? `
    <div class="form-group" style="flex:2">
      <label>${esc(t('pageProject.addRisk.category'))}</label>
      <input type="text" id="rm-category" value="${esc(r.category || '')}" list="rm-category-options" autocomplete="off" placeholder="${esc(t('pageProject.addRisk.categoryPlaceholder'))}">
      <datalist id="rm-category-options">${datalistOptions}</datalist>
    </div>
  `
    : `<div class="form-group" style="flex:2"><label>${esc(t('pageProject.addRisk.category'))}</label><p style="font-size:13px;padding:7px 0;color:var(--text2);margin:0">${esc(r.category || '—')}</p><input type="hidden" id="rm-category" value="${esc(r.category || '')}"></div>`;

  const body = `
    <div class="form-row">
      <div class="form-group" style="flex:1"><label>${esc(t('pageProject.addRisk.code'))} <span style="color:var(--text3);font-weight:400">${esc(t('pageProject.addRisk.codeNote'))}</span></label>
        <input type="text" id="rm-code" maxlength="5" value="${esc(r.code || '')}" placeholder="${esc(t('pageProject.addRisk.codePlaceholder'))}" style="text-transform:uppercase;font-family:var(--font-mono)"></div>
      ${categoryFieldHtml}
      <div class="form-group" style="flex:1"><label>${esc(t('pageProject.addRisk.visibility'))}</label>
        <select id="rm-visibility">
          <option value="publique" ${r.visibility === 'publique' ? 'selected' : ''}>${esc(t('pageProject.visibility.public'))}</option>
          <option value="privée"   ${r.visibility === 'privée' ? 'selected' : ''}>${esc(t('pageProject.visibility.private'))}</option>
        </select>
      </div>
    </div>
    <div class="form-group" style="margin-top:8px">
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-weight:400;margin:0">
        <input type="checkbox" id="rm-escalated" ${r.escalated_to_program ? 'checked' : ''} style="width:auto;margin:0">
        ${esc(t('pageProject.addRisk.escalateProgram'))}
      </label>
      <p style="font-size:12px;color:var(--text3);margin:4px 0 0">${esc(t('pageProject.addRisk.escalateProgramHelp'))}</p>
    </div>
    <div class="form-group" style="margin-top:8px"><label>${esc(t('pageProject.addRisk.cause'))}</label><textarea id="rm-cause" rows="2" placeholder="${esc(t('pageProject.addRisk.causePlaceholder'))}">${esc(r.cause || '')}</textarea></div>
    <div class="form-group" style="margin-top:8px"><label>${esc(t('pageProject.addRisk.event'))}</label><textarea id="rm-consequence" rows="2" placeholder="${esc(t('pageProject.addRisk.eventPlaceholder'))}">${esc(r.consequence || '')}</textarea></div>
    <div class="form-group" style="margin-top:8px"><label>${esc(t('pageProject.addRisk.stages'))}</label><div id="rm-stages">${stageChecks}</div></div>
    ${standardSection}`;

  openModal(
    isNew
      ? t('pageProject.addRisk.titleNew', { object: t('vocabulary.risk') })
      : t('pageProject.addRisk.titleEdit', { object: t('vocabulary.risk') }),
    body,
    async () => {
      const checkedStages = Array.from(
        document.querySelectorAll<HTMLInputElement>('input[name="risk-stages"]:checked'),
      ).map((c) => c.value);
      const codeEl = document.getElementById('rm-code') as HTMLInputElement | null;
      const causeEl = document.getElementById('rm-cause') as HTMLTextAreaElement | null;
      const consEl = document.getElementById('rm-consequence') as HTMLTextAreaElement | null;
      const catEl = document.getElementById('rm-category') as HTMLInputElement | null;
      const visEl = document.getElementById('rm-visibility') as HTMLSelectElement | null;
      const escEl = document.getElementById('rm-escalated') as HTMLInputElement | null;
      const data = {
        code: ((codeEl?.value || '') as string).toUpperCase().trim() || null,
        cause: causeEl?.value || '',
        consequence: consEl?.value || '',
        category: catEl?.value || '',
        visibility: visEl?.value || 'publique',
        stages: checkedStages,
        escalated_to_program: !!escEl?.checked,
      };
      try {
        if (!ps.project) return;
        if (isNew) {
          const newRisk = (await api.createProjectRisk(ps.project.id, data)) as PageRisk;
          (ps.risks || (ps.risks = [])).push(newRisk);
          (ps.stableOrder || (ps.stableOrder = [])).push(newRisk.id);
          (ps.entries || (ps.entries = {}))[newRisk.id] = {
            probability: 1,
            impact: 1,
            is_relevant: true,
            owner_id: null,
            actions: [],
            linked_actions: [],
            linked_action_ids: [],
          };

          const alsoStdEl = document.getElementById('rm-also-standard') as HTMLInputElement | null;
          const alsoStandard = !!alsoStdEl?.checked;
          const targetLists = alsoStandard
            ? Array.from(document.querySelectorAll<HTMLInputElement>('.rm-rl-chk:checked')).map(
                (c) => parseInt(c.value, 10),
              )
            : [];
          if (targetLists.length) {
            const stdPayload = {
              stages: data.stages,
              category: data.category,
              visibility: data.visibility,
              cause: data.cause,
              consequence: data.consequence,
            };
            const failed: number[] = [];
            for (const lid of targetLists) {
              try {
                await api.createStdRisk(lid, stdPayload);
              } catch (e: unknown) {
                failed.push(lid);
                safeWarn('createStdRisk ' + lid, e);
              }
            }
            if (failed.length === 0) {
              toast(t('pageProject.addRisk.copiedAll', { n: targetLists.length }));
            } else {
              toast(
                t('pageProject.addRisk.copiedPartial', {
                  ok: targetLists.length - failed.length,
                  total: targetLists.length,
                }),
                'error',
              );
            }
          }
        } else if (existingRisk) {
          const updated = (await api.updateProjectRisk(
            ps.project.id,
            existingRisk.id,
            data,
          )) as PageRisk;
          const arr = ps.risks || (ps.risks = []);
          const idx = arr.findIndex((rr) => rr.id === existingRisk.id);
          if (idx !== -1) arr[idx] = updated;
        }
        closeModal();
        toast(isNew ? t('pageProject.addRisk.toastNew') : t('pageProject.addRisk.toastEdit'));
        reRenderTable();
        updateStats();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('pageProject.addRisk.btnNew') : t('pageProject.addRisk.btnEdit'),
  );

  setTimeout(() => {
    const chk = document.getElementById('rm-also-standard') as HTMLInputElement | null;
    const panel = document.getElementById('rm-standard-lists') as HTMLElement | null;
    if (chk && panel) {
      chk.addEventListener('change', () => {
        panel.style.display = chk.checked ? '' : 'none';
      });
    }
  }, 0);
}

// ── ESM globals shim ─────────────────────────────────────────────────────
Object.assign(window, { showRiskHistory, renderHistoryChart, showAddRiskModal });

export {};
